// List-of-rows editor for a single tier of intake template items.
// Reused twice inside FulfillmentIntakeTemplatesSubTab (Tier 1 +
// Tier 2). Pure helpers in `src/lib/fulfillment/templateItem.ts` own
// the array operations (add / remove / move / rename); this component
// is a thin presentational wrapper around them.
//
// Slice 2: introduces the friendlier item-by-item authoring path that
// replaces the Slice-1 JSON textarea. Reorder uses up/down buttons
// (D&D deferred until pilots ask). Item ids are auto-derived from the
// label on add and stay stable across renames so live form responses
// keyed by id don't get orphaned.

import { useState, type KeyboardEvent } from "react";
import {
  addItem,
  moveItemDown,
  moveItemUp,
  removeItem,
  renameItemLabel,
  type IntakeTemplateItem,
} from "@/lib/fulfillment/templateItem";

type Props = {
  tier: 1 | 2;
  items: IntakeTemplateItem[];
  onChange: (next: IntakeTemplateItem[]) => void;
  disabled?: boolean;
};

const TIER_COPY = {
  1: {
    title: "Tier 1 — Mandatory inspection items",
    addPlaceholder: "Roof — no leaks, shingles intact",
    emptyHint:
      "No mandatory items yet. Each item becomes a Pass / N/A row on the fill modal; every item must be answered before a form can be approved.",
  },
  2: {
    title: "Tier 2 — High-value upgrade items",
    addPlaceholder: "Paint touch-up — match existing color",
    emptyHint:
      "No upgrade items yet. Each item becomes a checkbox on the fill modal; the reviewer elects which to send to work_in_progress.",
  },
} as const;

export function FulfillmentTemplateItemEditor({
  tier,
  items,
  onChange,
  disabled = false,
}: Props) {
  const [draft, setDraft] = useState("");
  const copy = TIER_COPY[tier];

  function handleAdd() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange(addItem(items, trimmed));
    setDraft("");
  }

  function handleDraftKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  }

  return (
    <fieldset className="rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-zinc-200">
        {copy.title} ({items.length})
      </legend>

      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">{copy.emptyHint}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li
              key={it.id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-800"
            >
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => onChange(moveItemUp(items, idx))}
                  disabled={disabled || idx === 0}
                  className="px-1 text-xs text-gray-500 hover:text-gray-900 disabled:opacity-30 dark:text-zinc-400 dark:hover:text-zinc-100"
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => onChange(moveItemDown(items, idx))}
                  disabled={disabled || idx === items.length - 1}
                  className="px-1 text-xs text-gray-500 hover:text-gray-900 disabled:opacity-30 dark:text-zinc-400 dark:hover:text-zinc-100"
                  aria-label="Move down"
                >
                  ↓
                </button>
              </div>
              <code className="rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-zinc-900 dark:text-zinc-400">
                {it.id}
              </code>
              <input
                type="text"
                value={it.label}
                onChange={(e) => onChange(renameItemLabel(items, it.id, e.target.value))}
                disabled={disabled}
                className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              />
              <button
                type="button"
                onClick={() => onChange(removeItem(items, it.id))}
                disabled={disabled}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:text-red-700 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:text-red-400"
                aria-label="Remove item"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleDraftKey}
          disabled={disabled}
          placeholder={copy.addPlaceholder}
          className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled || draft.trim().length === 0}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
        >
          Add item
        </button>
      </div>
    </fieldset>
  );
}
